import { similarAlgorithmType } from '@redux/widget/actions';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import AnalyticsConstants from '../../../../constants/analytics';
import SUPPORTED_PVREC_CATEGORY_CODES from '../../../../constants/pvrecCategoryCodes';
import TrackableContainer from '../../../components/trackable_container';
import Vendor from '../../../decorators/vendor';
import { codeToSlugNameMap } from '../../../utils/categoryNameMappings';
import algorithmAnalytics from '../constants/algorithm_analytics';
import algorithms from '../constants/algorithms';
import Recommendations from './card_container/Recommendations';
import SkeletonCards from './card_container/skeletonCards';
import Styles from './pvrec_widget.scss';
import { Wrapper } from './wrapper';

class WidgetContainer extends Component {
	header() {
		const {
			headerText: customHeaderText,
			category: { code: categoryCode, name: categoryName },
		} = this.props;
		if (customHeaderText) {
			return customHeaderText;
		}

		const shortNameExists =
			codeToSlugNameMap[categoryCode] &&
			codeToSlugNameMap[categoryCode].shortName;
		const headerText = shortNameExists
			? codeToSlugNameMap[categoryCode].shortName
			: categoryName;
		return `Other ${headerText} You Might\u00A0Like`;
	}

	noValidAlgorithm() {
		const { recommendedVendors } = this.props;
		return (
			!recommendedVendors[algorithms.PAIVER] &&
			!recommendedVendors[algorithms.PAIVER_ROS] &&
			!recommendedVendors[algorithms.SIMILAR_ROS] &&
			!recommendedVendors[algorithms.SIMILAR_VENDORS_BASED_ON_ACTIVITY]
		);
	}

	widgetViewedTrackingData() {
		const trackingProps = {
			...this.commonTrackingData(),
			event: AnalyticsConstants.WIDGET_VIEWED,
		};

		return trackingProps;
	}

	commonTrackingData() {
		const {
			marketCode,
			recommendedVendors,
			algorithmType,
			isLoggedIn,
			vendorToFindSimilar,
			headerText,
			category: { code: categoryCode },
		} = this.props;
		const vendors = recommendedVendors[algorithmType];
		if (!vendors || !vendors.length > 0) {
			return {
				properties: {},
			};
		}
		const recommendedVendor = new Vendor(vendors[0]);
		const analytics = algorithmAnalytics[algorithmType];
		const analyticsName = analytics.name || 'vrm';
		const sourceFormula =
			algorithmType === algorithms.SIMILAR_VENDORS_BASED_ON_ACTIVITY
				? algorithmAnalytics.SIMILAR_ROS.name
				: [algorithms.PAIVER, algorithms.PAIVER_ROS].includes(algorithmType) &&
						!isLoggedIn
					? `${analyticsName} anon`
					: analyticsName;
		let widgetNameExtension = '';

		if (
			[
				algorithms.SIMILAR_VENDORS_BASED_ON_ACTIVITY,
				algorithms.SIMILAR_ROS,
			].includes(algorithmType)
		) {
			widgetNameExtension = ` - ${
				vendorToFindSimilar.similarAlgorithmType ===
				similarAlgorithmType.LAST_LEAD
					? 'lead'
					: 'favorite'
			}`;
		} else if (headerText) {
			widgetNameExtension = ` - storefront`;
		}
		const isOnSemanticSearch = new URLSearchParams(window.location.search).has(
			'q',
		);

		const sourcePage = isOnSemanticSearch
			? 'semantic results'
			: 'category results';

		return {
			properties: {
				category_code:
					recommendedVendor.categoryCode === ''
						? categoryCode
						: recommendedVendor.categoryCode,
				displayMarketCode: marketCode,
				pagePosition: analytics.position,
				sourceChannel: 'internal',
				sourceContent: 'recommended vendors',
				sourceFormula,
				sourcePage: sourcePage,
				vendorCategory: recommendedVendor.categoryCode,
				vendorCount: recommendedVendors[algorithmType].length,
				widgetName: `right rail module${widgetNameExtension}`,
			},
		};
	}

	getImpressionType() {
		const { vendorToFindSimilar, headerText } = this.props;
		const impressionType = 'Rec Module: Directory - Right Rail Recs';

		if (headerText) {
			if (
				vendorToFindSimilar?.similarAlgorithmType ===
				similarAlgorithmType.LAST_LEAD
			) {
				return `${impressionType} - lead`;
			}

			if (
				vendorToFindSimilar?.similarAlgorithmType ===
				similarAlgorithmType.LAST_SAVED
			) {
				return `${impressionType} - favorite`;
			}

			return `${impressionType} - storefront`;
		}

		return impressionType;
	}

	render() {
		const { algorithmType, cardType, category, recommendedVendors } =
			this.props;

		if (
			this.noValidAlgorithm() ||
			!SUPPORTED_PVREC_CATEGORY_CODES.includes(category.code)
		) {
			return null;
		}

		return (
			<>
				{recommendedVendors[algorithmType]?.length > 0 ? (
					<TrackableContainer trackingData={this.widgetViewedTrackingData()}>
						<Wrapper
							className={Styles.pvrecWidgetWrapper}
							headerText={this.header()}
							seeAllUrl=""
							cardType={cardType}
							trackingData={this.commonTrackingData()}
							algorithmType={algorithmType}
						>
							<Recommendations
								algorithmType={algorithmType}
								impressionType={this.getImpressionType()}
								recommendedVendors={recommendedVendors[algorithmType]}
								cardType={cardType}
								trackingData={this.commonTrackingData()}
								displayTheme="city"
							/>
						</Wrapper>
					</TrackableContainer>
				) : (
					<Wrapper
						className={Styles.pvrecWidgetWrapper}
						headerText={this.header()}
						seeAllUrl=""
						cardType={cardType}
						trackingData={{ properties: {} }}
						algorithmType={algorithmType}
						noLiteHeaderSkeleton={true}
					>
						<SkeletonCards />
					</Wrapper>
				)}
			</>
		);
	}
}

export const propTypes = {
	algorithmType: PropTypes.oneOf([
		algorithms.PAIVER,
		algorithms.PAIVER_ROS,
		algorithms.SIMILAR_ROS,
		algorithms.SIMILAR_VENDORS_BASED_ON_ACTIVITY,
	]),
	cardType: PropTypes.oneOf(['sm', 'lg']),
	className: PropTypes.string,
	category: PropTypes.object.isRequired,
	fetchPvRecAnonVendors: PropTypes.func.isRequired,
	marketCode: PropTypes.string,
	location: PropTypes.object.isRequired,
	isLoggedIn: PropTypes.bool,
	recommendedVendors: PropTypes.arrayOf(PropTypes.object),
	vendorToFindSimilar: PropTypes.object,
	headerText: PropTypes.string,
};

WidgetContainer.propTypes = propTypes;

WidgetContainer.defaultProps = {
	algorithmType: algorithms.PAIVER,
	cardType: 'sm',
	className: '',
	marketCode: '',
	isLoggedIn: false,
	recommendedVendors: [],
	vendorToFindSimilar: null,
	headerText: '',
};

export default WidgetContainer;
